import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, BookmarkCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BookmarkButton — Round 84 (Feb 14, 2026).
 *
 * Renders on tutorial posts. Toggles a bookmark on the post and
 * shows the running `bookmark_count`. 50+ distinct bookmarks across
 * a user's account earns the Verified Expert badge.
 */
export default function BookmarkButton({ postId, initialBookmarked, initialCount, onToggled, isMember }) {
  const [marked, setMarked] = useState(Boolean(initialBookmarked));
  const [count, setCount] = useState(Number(initialCount || 0));
  const [pending, setPending] = useState(false);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMember) {
      toast.error('Join the community to bookmark tutorials.');
      return;
    }
    if (pending) return;
    setPending(true);
    try {
      const r = await axios.post(
        `${API}/community/posts/${postId}/bookmark`,
        {},
        { withCredentials: true },
      );
      setMarked(Boolean(r.data?.bookmarked));
      setCount(Number(r.data?.bookmark_count || 0));
      if (onToggled) onToggled(r.data);
      if (r.data?.bookmarked) {
        toast.success(r.data.is_verified_expert ? 'Bookmarked — you\'re a Verified Expert!' : 'Bookmarked.');
      } else {
        toast.success('Bookmark removed.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bookmark failed.');
    } finally {
      setPending(false);
    }
  };

  const Icon = marked ? BookmarkCheck : Bookmark;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      className={`text-xs flex items-center gap-1 transition ${
        marked
          ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700'
          : 'text-muted-foreground hover:text-foreground'
      } ${pending ? 'opacity-60' : ''}`}
      data-testid={`bookmark-btn-${postId}`}
      title={marked ? 'Remove bookmark' : 'Save for later (counts toward Verified Expert)'}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="tabular-nums">{count}</span>
    </button>
  );
}
